import { ArrowForwardIos } from '@interhyp/platform-icons';
import Tippy from '@tippyjs/react';
import _ from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { followCursor } from 'tippy.js';
import { DataGridHeaderRow } from "./components/DataGridHeaderRow/DataGridHeaderRow.js";
import { DataGridRowGroups } from "./components/DataGridRowGroups/DataGridRowGroups.js";
import { generateTemplate } from "./functions/generateTemplate.js";
import { useHydratedColumns } from "./hooks/useHydratedColumns.js";
import { useIndexedData } from "./hooks/useIndexedData.js";
import { useOrder } from "./hooks/useOrder.js";
import { useOverflowControl } from "./hooks/useOverflowControl.js";
import { useSortedGroups } from "./hooks/useSortedGroups.js";
import { useTop } from "./hooks/useTop.js";
import { DataGridProvider } from "./provider/DataGridProvider.js";
import { IconButton } from "../iconButton/IconButton.js";
import styles from "./DataGrid.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const defaultDecorator = row => row;
const defaultIndexer = (data, index) => index;
export function DataGrid({
  rowAlternating = false,
  stickyHeader = false,
  offsetTop = 0,
  columns,
  rowBody,
  contextMenu,
  rowHeight = 60,
  hasBody,
  rowDecorator = defaultDecorator,
  rowIndexer = defaultIndexer,
  rowData,
  groupedRowData,
  lastColumnFixed = false,
  className,
  handleOverflow = false,
  scrollDistance = 200,
  headerBackgroundColor,
  rowStateHandling,
  variant = 'WHITE'
}) {
  const ref = useRef(null);
  const dataToUse = useMemo(() => groupedRowData ?? [{
    data: rowData ?? []
  }], [groupedRowData, rowData]);
  const {
    activeColumn,
    mode,
    changeOrder,
    resetOrder
  } = useOrder();
  const indexGroups = useIndexedData(dataToUse, rowIndexer);
  const sortedGroups = useSortedGroups(indexGroups, columns, activeColumn, mode);
  const {
    hydratedColumns,
    activeRow,
    toggleRow,
    focusRow
  } = useHydratedColumns(columns, rowBody, hasBody, rowStateHandling);
  const gridTemplate = useMemo(() => generateTemplate(hydratedColumns), [hydratedColumns]);
  const columnCount = hydratedColumns.length;
  const {
    calculatedTop
  } = useTop(ref, offsetTop);
  const closeMenu = useCallback(() => {
    focusRow(null);
  }, [focusRow]);

  /**
   * Reset active column, if column is not existing anymore
   */
  useEffect(() => {
    const activeColumnConfig = columns.find(column => column.name === activeColumn);
    if (!activeColumnConfig && activeColumn) {
      resetOrder();
    }
  }, [resetOrder, columns, activeColumn]);
  const [isOverflowing, fullyScrolledLeft, startedScrolling] = useOverflowControl(handleOverflow, ref);
  const scrollNext = useCallback(() => {
    if (ref.current) {
      ref.current.scrollTo({
        left: ref.current.scrollLeft + scrollDistance,
        behavior: 'smooth'
      });
    }
  }, [ref, scrollDistance]);
  const scrollPrev = useCallback(() => {
    if (ref.current) {
      const newLeft = ref.current.scrollLeft - scrollDistance;
      ref.current.scrollTo({
        left: newLeft < 0 ? 0 : newLeft,
        behavior: 'smooth'
      });
    }
  }, [ref, scrollDistance]);
  const buttonStyle = {
    '--offset-top': stickyHeader && calculatedTop > 0 ? `${calculatedTop}px` : '0',
    '--data-grid-row-height': `${rowHeight}px`
  };
  return /*#__PURE__*/_jsx(DataGridProvider, {
    activeRow: activeRow,
    children: /*#__PURE__*/_jsxs("div", {
      className: _(styles.gridWrapper, {
        [styles.gridWrapperWithControl]: isOverflowing
      }, styles[variant]),
      children: [isOverflowing && startedScrolling ? /*#__PURE__*/_jsx("div", {
        className: styles.prevButton,
        style: buttonStyle,
        children: /*#__PURE__*/_jsx(IconButton, {
          className: _(styles.overflowScrollButton, styles.leftIcon),
          onClick: scrollPrev,
          "aria-label": "zur\xFCck",
          icon: ArrowForwardIos
        })
      }) : undefined, isOverflowing && !fullyScrolledLeft && !lastColumnFixed ? /*#__PURE__*/_jsx("div", {
        className: styles.nextButton,
        style: buttonStyle,
        children: /*#__PURE__*/_jsx(IconButton, {
          className: styles.overflowScrollButton,
          onClick: scrollNext,
          "aria-label": "weiter",
          icon: ArrowForwardIos
        })
      }) : undefined, /*#__PURE__*/_jsxs("div", {
        style: {
          gridTemplateColumns: gridTemplate,
          '--data-grid-row-height': `${rowHeight}px`
        },
        className: _(styles.dataGrid, className, {
          [styles.isAlternating]: rowAlternating,
          [styles.isSticky]: stickyHeader,
          [styles.isLastColumnFixed]: lastColumnFixed,
          [styles.isOverflowing]: isOverflowing
        }),
        ref: ref,
        children: [/*#__PURE__*/_jsx(DataGridHeaderRow, {
          dataGridRef: ref,
          lastColumnFixed: lastColumnFixed,
          activeColumn: activeColumn,
          columnCount: columnCount,
          hydratedColumns: hydratedColumns,
          mode: mode,
          offsetTop: offsetTop,
          rowAlternating: rowAlternating,
          stickyHeader: stickyHeader,
          changeOrder: changeOrder,
          someHaveBody: !!hasBody,
          isOverflowing: isOverflowing,
          fullyScrolledLeft: fullyScrolledLeft,
          nextButton: isOverflowing && !fullyScrolledLeft ? /*#__PURE__*/_jsx("div", {
            className: styles.nextButtonLastColumnFixed,
            children: /*#__PURE__*/_jsx(IconButton, {
              className: styles.overflowScrollButton,
              onClick: scrollNext,
              "aria-label": "weiter",
              icon: ArrowForwardIos
            })
          }) : undefined,
          headerBackgroundColor: headerBackgroundColor
        }), /*#__PURE__*/_jsx(DataGridRowGroups, {
          sortedGroups: sortedGroups,
          rowAlternating: rowAlternating,
          columnCount: columnCount,
          hydratedColumns: hydratedColumns,
          rowDecorator: rowDecorator,
          hasBody: hasBody,
          contextMenu: contextMenu,
          focusRow: focusRow,
          toggleRow: toggleRow,
          lastColumnFixed: lastColumnFixed,
          activeRow: activeRow,
          closeMenu: closeMenu,
          rowBody: rowBody,
          isOverflowing: isOverflowing,
          fullyScrolledLeft: fullyScrolledLeft
        })]
      }), contextMenu && /*#__PURE__*/_jsx(Tippy, {
        followCursor: "initial",
        plugins: [followCursor]
      })]
    })
  });
}